<script setup>
import {
  ref,
  computed,
  onBeforeMount,
  onMounted,
  onUnmounted,
  defineEmits,
} from "vue"
import { useI18n } from "vue-i18n"
import {
  PopoverGroup,
  Popover,
  PopoverButton,
  PopoverPanel,
} from "@headlessui/vue"
import SearchBar from "./SearchBar.vue"

const emit = defineEmits(["sign-up", "sign-in"])

const { t } = useI18n()

const top = ref(false)

const preloading = ref(true)
const featuredManufacturers = ref([])
const primaryNavigation = ref([])

onMounted(() => {
  document.addEventListener("scroll", handleScroll())
})
onUnmounted(() => {
  document.removeEventListener("scroll", handleScroll())
})
function handleScroll() {
  top.value = window.pageYOffset > 10 ? false : true
}
onBeforeMount(async () => {
  try {
    const response = await axios.get(`/hardware/manufacturers/search`, {
      params: {
        hasProductListings: true,
        featured: true,
        disablePagination: true,
      },
    })
    featuredManufacturers.value = response.data.items

    primaryNavigation.value = buildPrimaryNavigation()
  } finally {
    preloading.value = false
  }
})

const collections = computed(() => {
  return {
    panels: {
      id: "PANELS",
      sections: [
        {
          name: t("common.shop.hardware.navigation.primary.manufacturers"),
          items: featuredManufacturers.value
            .filter((o) => o.hasSolarPanels)
            .map((o) => {
              return {
                name: o.companyName,
                to: { name: "", params: { slug: o.slug } },
              }
            }),
        },
        {
          name: t("common.shop.hardware.navigation.primary.article-groups"),
          items: [],
        },
      ],
    },
    inverters: {
      id: "INVERTERS",
      sections: [
        {
          name: t("common.shop.hardware.navigation.primary.manufacturers"),
          items: featuredManufacturers.value
            .filter((o) => o.hasSolarInverters)
            .map((o) => {
              return {
                name: o.companyName,
                to: { name: "", params: { slug: o.slug } },
              }
            }),
        },
        {
          name: t("common.shop.hardware.navigation.primary.article-groups"),
          items: [],
        },
      ],
    },
    batteries: {
      id: "BATTERIES",
      sections: [
        {
          name: t("common.shop.hardware.navigation.primary.manufacturers"),
          items: featuredManufacturers.value
            .filter((o) => o.hasBatteries)
            .map((o) => {
              return {
                name: o.companyName,
                to: { name: "", params: { slug: o.slug } },
              }
            }),
        },
      ],
    },
    mounting_systems: {
      id: "MOUNTING_SYSTEMS",
      sections: [
        {
          name: t("common.shop.hardware.navigation.primary.manufacturers"),
          items: featuredManufacturers.value
            .filter((o) => o.hasBos)
            .map((o) => {
              return {
                name: o.companyName,
                to: { name: "", params: { slug: o.slug } },
              }
            }),
        },
      ],
    },
  }
})

function buildPrimaryNavigation() {
  return [
    {
      id: "categories",
      name: t("common.shop.hardware.navigation.primary.categories"),
      open: false,
      collections: [
        {
          name: t("common.api.products.collections.PANELS"),
          item: collections.value.panels.sections,
        },
        {
          name: t("common.api.products.collections.INVERTERS"),
          item: collections.value.inverters.sections,
        },
        {
          name: t("common.api.products.collections.BATTERIES"),
          item: collections.value.batteries.sections,
        },
        {
          name: t("common.api.products.collections.MOUNTING_SYSTEMS"),
          item: collections.value.mounting_systems.sections,
        },
      ],
    },
    {
      id: "panels",
      name: t("common.api.products.collections.PANELS"),
      open: false,
      sections: collections.value.panels.sections,
    },
    {
      id: "inverters",
      name: t("common.api.products.collections.INVERTERS"),
      open: false,
      sections: collections.value.inverters.sections,
    },
    {
      id: "batteries",
      name: t("common.api.products.collections.BATTERIES"),
      open: false,
      sections: collections.value.batteries.sections,
    },
    {
      id: "mounting-systems",
      name: t("common.api.products.collections.MOUNTING_SYSTEMS"),
      open: false,
      sections: collections.value.mounting_systems.sections,
    },
  ]
}

const secondaryNavigation = [
  {
    name: t("common.shop.hardware.navigation.secondary.products"),
    to: { name: "HardwareMarketplaceShopManufacturers" },
  },
  {
    name: t("common.shop.hardware.navigation.secondary.manufacturers"),
    to: { name: "HardwareMarketplaceShopManufacturers" },
  },
  {
    name: t("common.shop.hardware.navigation.secondary.suppliers"),
    to: { name: "HardwareMarketplaceShopSuppliers" },
  },
]

function openPopover(index) {
  clearTimeout(primaryNavigation.value[index].closeTimeout)
  primaryNavigation.value[index].open = true
}
function closePopover(index) {
  primaryNavigation.value[index].closeTimeout = setTimeout(() => {
    primaryNavigation.value[index].open = false
  }, 200) // Add a small delay for smoother interaction
}
function clearCloseTimeout(index) {
  clearTimeout(primaryNavigation.value[index].closeTimeout)
}
</script>

<template>
  <header
    class="fixed w-full z-30 md:bg-opacity-90 transition duration-300 ease-in-out"
    :class="{ 'bg-white backdrop-blur-sm shadow-lg': !top }"
  >
    <nav class="border-b border-gray-200 bg-white px-8" aria-label="Primary">
      <div class="container-fluid mx-auto">
        <div
          class="grid grid-cols-[auto_1fr_auto_auto] items-center xl:gap-6 py-4"
        >
          <!-- Site branding -->
          <div class="shrink-0 mr-4">
            <!-- Logo -->
            <router-link
              to="/"
              class="flex gap-2 items-center justify-self-start xl:justify-self-center"
              aria-label="projetsolaire"
            >
              <img
                class="w-10 h-10 md:w-12 md:h-12"
                src="https://images.ctfassets.net/1b6f0wuf6ave/42P3RP32nltWjyhI0fg0PY/57029d3dbc531bad21cd75c871078436/logo_official_1.svg"
              />
              <h2
                :class="dark && top ? 'text-gray-200' : 'text-gray-800'"
                class="hidden md:block h2 text-xl"
              >
                {{ $t("common.shop.hardware.navigation.title") }}
              </h2>
              <img
                src="@common/images/warehouse-icon.svg"
                class="h-10 -mb-2.5 xl:hidden"
              />
            </router-link>
          </div>

          <div class="bg-white hidden xl:block">
            <div class="container mx-auto">
              <SearchBar />
            </div>
          </div>
          <!-- Desktop navigation -->
          <nav class="hidden xl:flex md:grow">
            <ul class="flex grow justify-end flex-wrap items-center">
              <li>
                <button
                  :class="
                    dark && top
                      ? 'text-gray-200 hover:text-gray-400'
                      : 'text-gray-600 hover:text-gray-900'
                  "
                  class="rounded-full px-3 py-2 flex items-center transition duration-150 ease-in-out shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                  @click="$emit('sign-in')"
                >
                  <span>Connexion</span>
                </button>
              </li>
              <li>
                <button
                  class="rounded-full px-3.5 py-2 text-base font-medium text-white shadow-sm hover:bg-gray-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600 bg-gradient-to-r bg-gray-900 ml-3"
                  @click="$emit('sign-up')"
                >
                  <span>Retour</span>
                </button>
              </li>
            </ul>
          </nav>

          <div class="flex space-x-2 justify-self-end sm:space-x-4 xl:hidden">
            <button type="button" class="group">
              <span
                aria-label="Shopping cart"
                class="transition--hover flex size-11 shrink-0 items-center justify-center rounded-full p-2 no-underline group-hover:bg-text group-hover:fill-background relative bg-gray-200"
                ><svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  class="size-5 group-hover:text-white"
                >
                  <path
                    d="M1 1H5L7.68 14.39C7.77144 14.8504 8.02191 15.264 8.38755 15.5583C8.75318 15.8526 9.2107 16.009 9.68 16H19.4C19.8693 16.009 20.3268 15.8526 20.6925 15.5583C21.0581 15.264 21.3086 14.8504 21.4 14.39L23 6H6M10 21C10 21.5523 9.55228 22 9 22C8.44772 22 8 21.5523 8 21C8 20.4477 8.44772 20 9 20C9.55228 20 10 20.4477 10 21ZM21 21C21 21.5523 20.5523 22 20 22C19.4477 22 19 21.5523 19 21C19 20.4477 19.4477 20 20 20C20.5523 20 21 20.4477 21 21Z"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  ></path></svg
                ><span class="sr-only">Shopping cart</span
                ><!----></span
              ></button
            ><a
              aria-haspopup="true"
              aria-expanded="false"
              target="auth.login.show"
              class="group grid auto-cols-max grid-flow-col items-center font-medium no-underline hover:no-underline lg:gap-2"
              href="https://solarclarity.com/en/auth/login"
              ><span
                aria-label="Account"
                class="transition--hover flex size-11 shrink-0 items-center justify-center rounded-full p-2 no-underline group-hover:bg-text group-hover:fill-background bg-gray-200"
                target="auth.login.show"
                ><svg
                  id="user-icon"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  class="stroke-black size-5 group-hover:text-white"
                  target="auth.login.show"
                >
                  <path
                    d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  ></path></svg></span></a
            ><button type="button">
              <span
                aria-label="Toggle primary navigation"
                class="transition--hover flex size-11 shrink-0 items-center justify-center rounded-full p-2 no-underline group-hover:bg-text group-hover:fill-background bg-primary"
                ><svg
                  width="20"
                  height="20"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 10.71"
                  aria-hidden="true"
                  class="fill-black"
                >
                  <rect width="20" height="2.14"></rect>
                  <rect y="4.29" width="20" height="2.14"></rect>
                  <rect y="8.57" width="20" height="2.14"></rect></svg
              ></span>
            </button>
          </div>
        </div>
      </div>
    </nav>
    <div
      class="container-fluid border-b border-gray-200 bg-white py-2 px-4 block xl:hidden"
    >
      <SearchBar />
    </div>
    <div class="container-fluid mx-auto">
      <div
        class="relative px-8 w-full items-center justify-between gap-6 hidden xl:flex border-b border-gray-200 bg-white"
      >
        <PopoverGroup aria-labelledby="secondaryMenu" as="nav">
          <h2 id="secondaryMenu" class="sr-only">Main navigation</h2>
          <ul class="flex">
            <Popover
              v-for="(navigationItem, navigationItemIdx) in primaryNavigation"
              :key="navigationItemIdx"
              as="li"
              class="inline-flex items-center px-2 first:px-0 first:pr-3 2xl:px-3 first:2xl:px-0 first:2xl:pr-3 lg:text-sm 2xl:text-base"
            >
              <div class="flex">
                <PopoverButton
                  class="relative flex items-center gap-2 py-6 font-medium no-underline before:absolute before:bottom-0 before:block before:h-[3px] transition-all duration-300 underline-offset-8 decoration-2 decoration-orange-600 hover:underline focus:underline"
                  @mouseenter="openPopover(navigationItemIdx)"
                  @mouseleave="closePopover(navigationItemIdx)"
                >
                  {{ navigationItem.name }}
                  <svg
                    width="9"
                    height="6"
                    viewBox="0 0 9 6"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                    class="fill-black transition-all duration-100"
                  >
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M9 1.32596L7.72342 0L4.5 3.34808L1.27658 0L0 1.32596L4.5 6L9 1.32596Z"
                    ></path></svg
                ></PopoverButton>
              </div>
              <transition
                enter-active-class="transition ease-out duration-200"
                enter-from-class="opacity-0 translate-y-1"
                enter-to-class="opacity-100 translate-y-0"
                leave-active-class="transition ease-in duration-150"
                leave-from-class="opacity-100 translate-y-0"
                leave-to-class="opacity-0 translate-y-1"
              >
                <div
                  v-show="navigationItem.open"
                  class="absolute transform inset-x-0 top-16 z-10 bg-white shadow-lg ring-1 ring-gray-900/5"
                  @mouseenter="clearCloseTimeout(navigationItemIdx)"
                  @mouseleave="closePopover(navigationItemIdx)"
                >
                  <div
                    class="mx-auto grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-6 py-10 lg:grid-cols-2 lg:px-8"
                  >
                    <div class="grid grid-cols-2 gap-x-6 sm:gap-x-8">
                      <div
                        v-for="section in navigationItem.sections"
                        :key="section.id"
                      >
                        <h3 class="text-sm font-medium leading-6 text-gray-900">
                          {{ section.name }}
                        </h3>
                        <div class="mt-6 flow-root">
                          <div class="-my-2">
                            <a
                              v-for="item in section.items"
                              :key="item.name"
                              :href="item.href"
                              class="flex gap-x-4 py-2 text-sm font-medium leading-6 text-gray-700 hover:underline"
                            >
                              {{ item.name }}
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </transition>
            </Popover>
          </ul>
        </PopoverGroup>
      </div>
    </div>
  </header>
</template>
